import { useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Info, Warning, WarningOctagon } from "@phosphor-icons/react";
import {
  selectTherapistProfile,
  addAcknowledgedSystemMessage,
} from "../../store/slices/authSlice";
import {
  useGetActiveSystemMessagesQuery,
  useAcknowledgeSystemMessageMutation,
} from "../../store/services/therapistApi";
import "./SystemMessagePopup.css";

const TYPE_ICON = {
  info: Info,
  warning: Warning,
  critical: WarningOctagon,
};

/**
 * Global entry popup for NiV-authored system broadcasts.
 * Shows one unacknowledged message at a time (oldest first). After the user
 * clicks "הבנתי" the message ID is persisted to their therapist doc so it
 * never shows again. Rendered once in DashboardLayout.
 */
function SystemMessagePopup() {
  const dispatch = useDispatch();
  const therapistProfile = useSelector(selectTherapistProfile);
  const uid = therapistProfile?.uid;

  // Skip until auth resolves to avoid premature permission-denied reads.
  const { data: messages = [] } = useGetActiveSystemMessagesQuery(undefined, {
    skip: !uid,
  });
  const [acknowledgeSystemMessage, { isLoading: acknowledging }] =
    useAcknowledgeSystemMessageMutation();

  // Locally-acked IDs so we can advance to the next message immediately,
  // before the therapist profile refetch lands.
  const [justAcked, setJustAcked] = useState([]);

  const currentMessage = useMemo(() => {
    if (!messages.length) return null;
    const acked = new Set([
      ...(therapistProfile?.acknowledgedSystemMessages || []),
      ...justAcked,
    ]);
    return messages.find((m) => !acked.has(m.id)) || null;
  }, [messages, therapistProfile?.acknowledgedSystemMessages, justAcked]);

  if (!uid || !currentMessage) return null;

  const type = TYPE_ICON[currentMessage.type] ? currentMessage.type : "info";
  const IconComponent = TYPE_ICON[type];

  const handleAcknowledge = async () => {
    const messageId = currentMessage.id;
    // Advance UI immediately, then persist.
    setJustAcked((prev) => [...prev, messageId]);
    dispatch(addAcknowledgedSystemMessage(messageId));
    try {
      await acknowledgeSystemMessage({ uid, messageId }).unwrap();
    } catch (error) {
      // Already logged in the RTK queryFn (CK-7038). Persistence will retry on
      // next entry since the message stays active until acked server-side.
    }
  };

  return (
    <div className="system-message-overlay">
      <div className={`system-message-modal system-message-modal--${type}`}>
        <div className={`system-message-icon system-message-icon--${type}`}>
          <IconComponent size={30} weight="duotone" />
        </div>

        <h3 className="system-message-title">{currentMessage.title}</h3>
        <p className="system-message-body">{currentMessage.body}</p>

        <button
          className={`system-message-btn system-message-btn--${type}`}
          onClick={handleAcknowledge}
          disabled={acknowledging}
        >
          הבנתי
        </button>
      </div>
    </div>
  );
}

export default SystemMessagePopup;
